import { useEffect, useMemo, useState } from 'react'
import { Select } from 'antd'
import classNames from 'classnames'
import Modal from '@common/Modal'
import Button from '@UI/New/Button'
import {
  directionGroupApi,
  directionOrdersApi,
  reportAPI,
  usersAPI,
} from '@/api'
import { useAddAlert } from '@/hooks/useAddAlert'
import { IcoRemove } from '@/utils/icons'
import Preloader from '@UI/Preloader'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import s from './removeDirectionModal.module.css'

// Ученик приходит подписанным как «Имя (педагог)», а педагог тут и так известен.
const stripTeacherSuffix = (label) =>
  String(label ?? '').replace(/\s*\([^()]*\)\s*$/, '')

const RemoveDirectionModalContent = ({
  subject,
  groups,
  teacherId,
  onClose,
  onConfirm,
}) => {
  const addAlert = useAddAlert()
  const [colleagues, setColleagues] = useState([])
  const [students, setStudents] = useState([])
  const [assignments, setAssignments] = useState({})
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  const directionId = subject?.id

  useEffect(() => {
    if (!directionId) return

    setAssignments({})
    setLoading(true)
    // ponytail: один список педагогов на курс без фильтра по франшизе группы —
    // недопустимый вариант отклонит бэкенд с понятным текстом.
    Promise.all([
      usersAPI.getColleaguesForGroup({ name: '', directionId }),
      directionOrdersApi.getPrivateOwners(teacherId, directionId),
    ])
      .then(([colleaguesRes, studentsRes]) => {
        setColleagues(
          (colleaguesRes?.data?.data ?? [])
            .map((colleague) => ({
              label: colleague?.name,
              value: Number(colleague?.id),
            }))
            .filter((colleague) => colleague.value !== Number(teacherId)),
        )
        const studentsData = Array.isArray(studentsRes?.data)
          ? studentsRes.data
          : studentsRes?.data?.data ?? []

        setStudents(
          studentsData.map((student) => ({
            id: Number(student?.value),
            name: stripTeacherSuffix(student?.label),
          })),
        )
      })
      .finally(() => setLoading(false))
  }, [directionId, teacherId])

  const rows = useMemo(
    () => [
      ...groups.map((group) => ({
        key: `group-${group?.id}`,
        type: 'group',
        id: group?.id,
        name: group?.name,
        hint: 'Группа',
      })),
      ...students.map((student) => ({
        key: `student-${student.id}`,
        type: 'student',
        id: student.id,
        name: student.name,
        hint: 'Индивидуальный ученик',
      })),
    ],
    [groups, students],
  )

  const isFilled = rows.every((row) => assignments[row.key])

  const reassignRow = (row) => {
    const newTeacherId = assignments[row.key]

    if (row.type === 'group') {
      return directionGroupApi
        .updGroupTeacher({ id: row.id, teacher_id: newTeacherId })
        .then((res) => Number(res?.data?.teacher_id) === Number(newTeacherId))
    }

    const data = new FormData()
    data.append('student_id', String(row.id))
    data.append('course_id', String(directionId))
    data.append('teacher_id', String(newTeacherId))

    return reportAPI.private
      .updateTeacher(data)
      .then((res) => res?.data?.success === true)
  }

  const handleConfirm = async () => {
    setSubmitting(true)

    try {
      for (const row of rows) {
        // Последовательно, чтобы не удалять курс после частичного переноса.
        // eslint-disable-next-line no-await-in-loop
        const reassigned = await reassignRow(row)

        if (!reassigned) {
          addAlert({
            title: 'Ошибка',
            text: `Не удалось сменить педагога: ${row.name}`,
            type: 'error',
          })
          return
        }
      }

      if (await onConfirm()) {
        onClose()
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={classNames(mclasses.modalContainer, s.modalContainer)}>
      <div className={mclasses.modalHeader}>
        <div className={mclasses.modalTitleContainer}>
          <span className={mclasses.modalHeaderTitle}>Удаление курса</span>
        </div>
        <div className={mclasses.closeIcon} onClick={onClose}>
          <IcoRemove />
        </div>
      </div>
      <div className={mclasses.formWrapper}>
        {loading ? (
          <Preloader />
        ) : (
          <>
            <p className={mclasses.modalDescription}>
              {rows.length
                ? `Чтобы удалить курс «${subject?.name}», выберите нового педагога для групп и индивидуальных учеников.`
                : `Удалить курс «${subject?.name}» у сотрудника?`}
            </p>
            {rows.map((row) => (
              <div key={row.key} className={s.row}>
                <div>
                  <div className={s.rowName}>{row.name || '—'}</div>
                  <div className={s.rowHint}>{row.hint}</div>
                </div>
                <Select
                  showSearch
                  className={classNames(iclasses.newSelect)}
                  popupClassName={classNames(iclasses.newSelectPopup)}
                  placeholder="Выберите педагога"
                  value={assignments[row.key] ?? null}
                  onChange={(value) =>
                    setAssignments((state) => ({ ...state, [row.key]: value }))
                  }
                  options={colleagues}
                  notFoundContent="Нет других педагогов на курсе"
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </div>
            ))}
          </>
        )}
      </div>
      <div className={mclasses.actions}>
        <Button color="gray" onClick={onClose}>
          Отменить
        </Button>
        <Button
          color="primary"
          onClick={handleConfirm}
          disabled={loading || submitting || !isFilled}
          isLoading={submitting}
        >
          {rows.length ? 'Сменить и удалить' : 'Удалить'}
        </Button>
      </div>
    </div>
  )
}

const RemoveDirectionModal = (props) => (
  <Modal
    active={props.open}
    className={classNames(mclasses.modalWrapper, s.modalWrapper)}
    setActive={props.onClose}
  >
    <RemoveDirectionModalContent {...props} />
  </Modal>
)

export default RemoveDirectionModal
